import React, { useCallback, useEffect, useMemo, useState } from "react";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import Swal from "sweetalert2";
import SearchableSelect from "../../components/common/SearchableSelect";
import { resolveAssetUrl } from "../../api/config";

const CertificatesManager = () => {
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedEvent, setSelectedEvent] = useState("");
  const [eventsForDate, setEventsForDate] = useState([]);
  const [results, setResults] = useState([]);
  const [, setLoadingEvents] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [certificateLogos, setCertificateLogos] = useState([]);
  const [loadingLogos, setLoadingLogos] = useState(false);
  const [savingLogos, setSavingLogos] = useState(false);
  const [logoForm, setLogoForm] = useState({ name: "", file: null });

  const [signatures, setSignatures] = useState({
    authorised: { name: "", path: "", file: null },
    president: { name: "", path: "", file: null },
    secretary: { name: "", path: "", file: null },
    convenor: { name: "", path: "", file: null },
  });
  const [loadingSignatures, setLoadingSignatures] = useState(false);
  const [savingSignatureKey, setSavingSignatureKey] = useState(null);
  
  const [allEvents, setAllEvents] = useState([]);
  const [sigEvent, setSigEvent] = useState("");

  useEffect(() => {
    API.get("/admin/events")
      .then((res) => {
        setAllEvents(res?.data || res || []);
      })
      .catch((err) => {
        console.error("Failed to load all events:", err);
      });
  }, []);
  const selectedEventObj = eventsForDate.find((e) => String(e.id) === String(selectedEvent));
  const convenorName = String(selectedEventObj?.convenor_name || "").trim();
  const activeCustomLogoCount = useMemo(
    () => certificateLogos.filter((logo) => !!logo.is_active).length,
    [certificateLogos]
  );

  // load dates' events using your /events-by-date endpoint
  const loadEventsByDate = useCallback(async () => {
    if (!selectedDate) {
      setEventsForDate([]);
      return;
    }
    try {
      setLoadingEvents(true);
      const res = await API.fetchEventsByDate(selectedDate);
      setEventsForDate(res?.data || res || []);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load events for date", "error");
    } finally {
      setLoadingEvents(false);
    }
  }, [selectedDate]);

  async function handleDownloadCertificate(registrationId) {
    const { value: action } = await Swal.fire({
        title: 'Download Certificate',
        input: 'radio',
        inputOptions: {
            'download': 'Download (existing PDF)',
            'correction': 'Correction (update data, keep old signatures)',
            'regenerate': 'Regenerate (update data, use current signatures)'
        },
        inputValue: 'download',
        showCancelButton: true,
        confirmButtonText: 'Proceed',
        confirmButtonColor: '#0d3373',
    });

    if (!action) return;

    const regenerate = action === 'correction' || action === 'regenerate';
    const keep_signatures = action === 'correction';

    try {
        if (regenerate) {
            Swal.fire({ title: "Generating certificate...", didOpen: () => Swal.showLoading() });
        }
        const { blob, filename } = await API.downloadRegistrationCertificate(registrationId, {
          regenerate,
          keep_signatures,
        });
        if (regenerate) Swal.close();

        // Create a link to download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || `certificate_${registrationId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        Swal.fire("Downloaded", "Certificate downloaded", "success");
    } catch (err) {
        if (regenerate) Swal.close();
        console.error("Download cert error:", err);
        Swal.fire("Error", err?.message || "Failed to download certificate", "error");
    }
  }

  const loadResults = useCallback(async () => {
    if (!selectedEvent) {
      setResults([]);
      return;
    }
    try {
      setLoadingResults(true);
      const res = await API.fetchCertificateResultsByEvent(selectedEvent);
      setResults(res?.data || res || []);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load registrations/results", "error");
    } finally {
      setLoadingResults(false);
    }
  }, [selectedEvent]);

  const loadCertificateLogos = useCallback(async () => {
    try {
      setLoadingLogos(true);
      const rows = await API.fetchCertificateLogos();
      setCertificateLogos(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error(err);
      setCertificateLogos([]);
      Swal.fire("Error", "Failed to load certificate logos", "error");
    } finally {
      setLoadingLogos(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) loadEventsByDate();
  }, [selectedDate, loadEventsByDate]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  useEffect(() => {
    loadCertificateLogos();
  }, [loadCertificateLogos]);

  const loadSignatures = useCallback(async () => {
    try {
      setLoadingSignatures(true);
      const rows = await API.fetchSignatures({ event_id: sigEvent || undefined });
      const newSigs = {
        authorised: { name: "", path: "", file: null },
        president: { name: "", path: "", file: null },
        secretary: { name: "", path: "", file: null },
        convenor: { name: "", path: "", file: null },
      };
      if (Array.isArray(rows)) {
        rows.forEach((row) => {
          if (newSigs[row.key]) {
            newSigs[row.key] = {
              name: row.name || "",
              path: row.path || "",
              file: null,
            };
          }
        });
      }
      setSignatures(newSigs);
    } catch (err) {
      console.error("Failed to load signatures:", err);
    } finally {
      setLoadingSignatures(false);
    }
  }, [sigEvent]);

  useEffect(() => {
    loadSignatures();
  }, [loadSignatures]);

  const handleSignatureChange = (key, field, value) => {
    setSignatures((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

  const handleSaveSignature = async (key) => {
    const sig = signatures[key];
    try {
      setSavingSignatureKey(key);
      const fd = new FormData();
      fd.append("key", key);
      if (sig.name !== undefined && sig.name !== null) {
        fd.append("name", sig.name.trim());
      }
      if (sig.file) {
        fd.append("signature", sig.file);
      }
      if (sigEvent) {
        fd.append("event_id", sigEvent);
      }
      await API.updateSignature(fd);
      await loadSignatures();
      Swal.fire("Saved", `Signature for ${key.toUpperCase()} updated.`, "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err?.message || "Failed to save signature", "error");
    } finally {
      setSavingSignatureKey(null);
    }
  };

  const handleSingle = async (row) => {
    await handleDownloadCertificate(row.registration_id);
  };

  const handleBulk = async () => {
    if (!selectedEvent) {
      Swal.fire("Select event first");
      return;
    }
    if (!results.length) {
      Swal.fire("No registrations/results for this event");
      return;
    }
    try {
      await API.downloadEventCertificatesZip(selectedEvent);
      Swal.fire("Downloaded", "All certificates zip downloaded", "success");
    } catch (err) {
      console.error("Bulk cert download error:", err);
      Swal.fire("Error", err?.message || "Failed to generate/download certificates zip", "error");
    }
  };

  const handleLogoCreate = async (e) => {
    e.preventDefault();
    if (!logoForm.name.trim() || !logoForm.file) {
      Swal.fire("Missing data", "Please enter logo name and select an image", "warning");
      return;
    }

    try {
      setSavingLogos(true);
      const fd = new FormData();
      fd.append("name", logoForm.name.trim());
      fd.append("logo", logoForm.file);
      await API.createCertificateLogo(fd);
      setLogoForm({ name: "", file: null });
      await loadCertificateLogos();
      Swal.fire("Saved", "Logo added for certificates", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err?.message || "Failed to add logo", "error");
    } finally {
      setSavingLogos(false);
    }
  };

  const handleLogoToggleActive = async (logo) => {
    try {
      setSavingLogos(true);
      const fd = new FormData();
      fd.append("name", String(logo.name || ""));
      fd.append("is_active", logo.is_active ? "0" : "1");
      await API.updateCertificateLogo(logo.id, fd);
      await loadCertificateLogos();
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to update logo status", "error");
    } finally {
      setSavingLogos(false);
    }
  };

  const handleLogoDelete = async (logo) => {
    const result = await Swal.fire({
      title: "Delete logo?",
      text: `This will remove "${logo.name}" from certificate layouts.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;

    try {
      setSavingLogos(true);
      await API.deleteCertificateLogo(logo.id);
      await loadCertificateLogos();
      Swal.fire("Deleted", "Certificate logo removed", "success");
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to delete logo", "error");
    } finally {
      setSavingLogos(false);
    }
  };

  const handleLogoReorder = async (logoId, direction) => {
    const current = Array.isArray(certificateLogos) ? [...certificateLogos] : [];
    const index = current.findIndex((row) => Number(row.id) === Number(logoId));
    if (index < 0) return;

    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= current.length) return;

    const tmp = current[index];
    current[index] = current[swapIndex];
    current[swapIndex] = tmp;

    try {
      setSavingLogos(true);
      await API.reorderCertificateLogos(current.map((row) => row.id));
      await loadCertificateLogos();
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to reorder logos", "error");
    } finally {
      setSavingLogos(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="max-w-6xl mx-auto p-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          🏅 Certificates Manager
        </h1>
        <p className="text-gray-500 mb-6">
          Choose date and event, check player results, and generate certificates.
        </p>

        <div className="bg-white rounded-2xl shadow-md p-6 mb-6 border border-gray-200 space-y-4">
          <h2 className="text-lg font-semibold text-gray-800">Additional Certificate Logos</h2>
          <p className="text-sm text-gray-600">
            Add custom logos and reorder them. Active logos are placed on certificates in this exact order.
            Active logos: <span className="font-semibold">{activeCustomLogoCount}</span>
          </p>

          <form onSubmit={handleLogoCreate} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block mb-1 text-sm font-semibold text-gray-700">Logo Name</label>
              <input
                type="text"
                value={logoForm.name}
                onChange={(e) => setLogoForm((prev) => ({ ...prev, name: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2"
                placeholder="Example: Govt Emblem"
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-semibold text-gray-700">Logo Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setLogoForm((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2"
              />
            </div>
            <button
              type="submit"
              disabled={savingLogos}
              className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-60"
            >
              {savingLogos ? "Saving..." : "Add Logo"}
            </button>
          </form>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-100 rounded-xl">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-600 uppercase">Order</th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-600 uppercase">Logo</th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-600 uppercase">Name</th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-600 uppercase">Status</th>
                  <th className="px-3 py-2 text-right text-xs font-bold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {loadingLogos ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center text-gray-500">Loading logos...</td>
                  </tr>
                ) : certificateLogos.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center text-gray-500">No additional logos added.</td>
                  </tr>
                ) : (
                  certificateLogos.map((logo, idx) => (
                    <tr key={logo.id}>
                      <td className="px-3 py-2 text-sm font-semibold text-gray-700">#{idx + 1}</td>
                      <td className="px-3 py-2">
                        <img
                          src={resolveAssetUrl(logo.logo_path, { folder: "certificate-logos" })}
                          alt={logo.name || "logo"}
                          className="h-10 w-auto object-contain"
                        />
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">{logo.name}</td>
                      <td className="px-3 py-2 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${logo.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                          {logo.is_active ? "Active" : "Hidden"}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleLogoReorder(logo.id, "up")}
                            disabled={savingLogos || idx === 0}
                            className="px-2 py-1 rounded bg-slate-200 text-slate-800 text-xs disabled:opacity-50"
                          >
                            Up
                          </button>
                          <button
                            type="button"
                            onClick={() => handleLogoReorder(logo.id, "down")}
                            disabled={savingLogos || idx === certificateLogos.length - 1}
                            className="px-2 py-1 rounded bg-slate-200 text-slate-800 text-xs disabled:opacity-50"
                          >
                            Down
                          </button>
                          <button
                            type="button"
                            onClick={() => handleLogoToggleActive(logo)}
                            disabled={savingLogos}
                            className="px-2 py-1 rounded bg-amber-500 text-white text-xs disabled:opacity-50"
                          >
                            {logo.is_active ? "Hide" : "Show"}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleLogoDelete(logo)}
                            disabled={savingLogos}
                            className="px-2 py-1 rounded bg-red-600 text-white text-xs disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Signatures Manager section */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-6 border border-gray-200 space-y-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-gray-150 pb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <span>✍️</span> Manage Authorized Signatures
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Configure signee names and upload signature images to be embedded in ID cards and merit certificates.
              </p>
            </div>
            
            <div className="w-full md:w-80">
              <label className="block mb-1 text-xs font-semibold text-gray-650">Signature Scope (Event Scope)</label>
              <select
                value={sigEvent}
                onChange={(e) => setSigEvent(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-xl px-3 py-2 bg-white text-slate-800"
              >
                <option value="">Global Default Signatures</option>
                {allEvents.map((evt) => (
                  <option key={evt.id} value={evt.id}>
                    {evt.title} • {evt.sport_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loadingSignatures ? (
            <div className="text-center py-6 text-gray-500">Loading signatures...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  key: "authorised",
                  title: "Authorized Signature",
                  desc: "For Player ID & Escort ID cards",
                  accentClass: "border-t-4 border-indigo-500",
                  bgBadge: "bg-indigo-50 text-indigo-700 border border-indigo-100",
                },
                {
                  key: "president",
                  title: "President Signature",
                  desc: "Used on certificates",
                  accentClass: "border-t-4 border-emerald-500",
                  bgBadge: "bg-emerald-50 text-emerald-700 border border-emerald-100",
                },
                {
                  key: "secretary",
                  title: "Secretary Signature",
                  desc: "Used on certificates",
                  accentClass: "border-t-4 border-amber-500",
                  bgBadge: "bg-amber-50 text-amber-700 border border-amber-100",
                },
                {
                  key: "convenor",
                  title: "Convenor Signature",
                  desc: "Used on certificates",
                  accentClass: "border-t-4 border-rose-500",
                  bgBadge: "bg-rose-50 text-rose-700 border border-rose-100",
                },
              ].map((cfg) => {
                const sig = signatures[cfg.key] || { name: "", path: "", file: null };
                const isSaving = savingSignatureKey === cfg.key;
                return (
                  <div
                    key={cfg.key}
                    className={`bg-slate-50/50 rounded-2xl border border-gray-150 p-5 flex flex-col justify-between hover:shadow-lg transition-all duration-300 ${cfg.accentClass}`}
                  >
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${cfg.bgBadge}`}>
                            {cfg.title}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400">{cfg.desc}</p>
                      </div>

                      {/* Current Signature Image */}
                      <div className="h-24 w-full border border-dashed border-gray-300 bg-white rounded-xl flex items-center justify-center overflow-hidden p-2">
                        {sig.path ? (
                          <img
                            src={resolveAssetUrl(sig.path)}
                            alt={`${cfg.title} preview`}
                            className="h-full w-auto object-contain"
                          />
                        ) : (
                          <span className="text-xs text-gray-400 italic">No image uploaded</span>
                        )}
                      </div>

                      {/* Input: Signee Name */}
                      <div>
                        <label className="block mb-1 text-xs font-semibold text-gray-600">Signee Name</label>
                        <input
                          type="text"
                          value={sig.name}
                          onChange={(e) => handleSignatureChange(cfg.key, "name", e.target.value)}
                          className="w-full text-sm border border-gray-300 rounded-xl px-3 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          placeholder="Enter name"
                        />
                      </div>

                      {/* Input: File Upload */}
                      <div>
                        <label className="block mb-1 text-xs font-semibold text-gray-600">Upload Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleSignatureChange(cfg.key, "file", e.target.files?.[0] || null)}
                          className="w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-gray-150 file:text-gray-700 hover:file:bg-gray-200 cursor-pointer"
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      disabled={isSaving}
                      onClick={() => handleSaveSignature(cfg.key)}
                      className="w-full mt-4 py-2 px-3 text-xs font-bold rounded-xl bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-60 transition-colors"
                    >
                      {isSaving ? "Saving..." : "Save Signature"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Date + Event selection */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-6 border border-gray-200 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block mb-1 text-sm font-semibold text-gray-700">
                Select Date
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-emerald-500"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setSelectedEvent("");
                  setResults([]);
                }}
              />
            </div>

            <div className="flex-1">
              <label className="block mb-1 text-sm font-semibold text-gray-700">
                Select Event
              </label>
              <SearchableSelect
                className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-emerald-500"
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
                disabled={!eventsForDate.length}
              >
                <option value="">-- Choose --</option>
                {eventsForDate.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title} • {ev.sport_name}
                  </option>
                ))}
              </SearchableSelect>
            </div>
          </div>
          {selectedEvent ? (
            <div className="text-sm text-gray-600 mt-2 space-y-2">
              <p>
                Convenor (from event): <span className="font-semibold">{convenorName || "-"}</span>
              </p>
              <div className="flex flex-wrap gap-6 items-center">
                <div className="flex items-center gap-2">
                  <span className="font-medium">District Logo:</span>
                  {selectedEventObj?.district_logo_path ? (
                    <img
                      src={resolveAssetUrl(selectedEventObj.district_logo_path, { folder: "districts" })}
                      alt="District logo"
                      className="h-10 object-contain"
                    />
                  ) : (
                    <span className="text-gray-500">Not added</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Sponsor:</span>
                  <span>{selectedEventObj?.sponsor_name || "Not added"}</span>
                  {selectedEventObj?.sponsor_logo ? (
                    <img
                      src={resolveAssetUrl(selectedEventObj.sponsor_logo, { folder: "events/sponsors" })}
                      alt={selectedEventObj?.sponsor_name || "Sponsor logo"}
                      className="h-10 object-contain"
                    />
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          <div className="flex justify-end">
            <button
              onClick={handleBulk}
              disabled={!selectedEvent || !results.length}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold shadow-md hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              📦 Generate All Certificates
            </button>
          </div>
        </div>

        {/* Results table */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">
              Players & Results
            </h2>
            {loadingResults && (
              <span className="text-sm text-gray-500">Loading...</span>
            )}
          </div>

          {(!results.length && !loadingResults) ? (
            <div className="p-8 text-center text-gray-500">
              {selectedEvent ? "No registrations/results for this event" : "Select date and event"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Player
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Game
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Time / Mark
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Certificate
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {results.map((row, idx) => (
                    <tr key={`${row.registration_id || "reg"}_${row.game_id || "game"}_${idx}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-800">
                          {row.name}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.classification_name || row.classification || "Unclassified"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.game_name || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.result_time || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-800">
                        {row.result_position || "-"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleSingle(row)}
                          className="px-4 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-full hover:bg-emerald-700 shadow-sm"
                        >
                          📄 Generate
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CertificatesManager;
